import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Slider,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  DateRange as DateRangeIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Tag as TagIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { ja } from 'date-fns/locale';

export interface SearchFilterOptions {
  searchTerm: string;
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  status: string[];
  priority: string[];
  tags: string[];
  assignedTo: string[];
  messageCount: {
    min: number;
    max: number;
  };
  responseTime: {
    min: number;
    max: number;
  };
  quickFilters: string[];
}

interface SearchFiltersProps {
  filters: SearchFilterOptions;
  onFiltersChange: (filters: SearchFilterOptions) => void;
  onSearch: (searchTerm: string) => void;
  availableTags: string[];
  availableOperators: { id: string; name: string }[];
  compactMode?: boolean;
}

const QUICK_FILTERS = [
  { id: 'unread', label: '未読', icon: '📬' },
  { id: 'urgent', label: '緊急', icon: '🔥' },
  { id: 'today', label: '今日', icon: '📅' },
  { id: 'no-response', label: '未返信', icon: '💬' },
  { id: 'long-wait', label: '長時間待機', icon: '⏰' },
];

export const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'pending', label: '未対応' },
  { value: 'active', label: '対応中' },
  { value: 'waiting', label: '返信待ち' },
  { value: 'completed', label: '完了' },
  { value: 'temp_completed', label: '一時完了' },
  { value: 'paused', label: '保留' },
  { value: 'archived', label: '削除' },
];

const PRIORITY_OPTIONS = [
  { value: 'high', label: '高' },
  { value: 'medium', label: '中' },
  { value: 'low', label: '低' },
];

export const SearchFilters: React.FC<SearchFiltersProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  availableTags,
  availableOperators,
  compactMode = false,
}) => {
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(
    new Set(['search', 'quick'])
  );

  // 検索テキスト変更
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    onFiltersChange({ ...filters, searchTerm });
    onSearch(searchTerm);
  };

  // クイックフィルター切り替え
  const toggleQuickFilter = (filterId: string) => {
    const newQuickFilters = filters.quickFilters.includes(filterId)
      ? filters.quickFilters.filter(f => f !== filterId)
      : [...filters.quickFilters, filterId];
    
    onFiltersChange({ ...filters, quickFilters: newQuickFilters });
  };

  // 日付範囲設定
  const handleDateRangeChange = (field: 'start' | 'end', date: Date | null) => {
    onFiltersChange({
      ...filters,
      dateRange: { ...filters.dateRange, [field]: date },
    });
  };

  // 複数選択フィールドの変更
  const handleMultiSelectChange = (
    field: keyof Pick<SearchFilterOptions, 'status' | 'priority' | 'tags' | 'assignedTo'>,
    value: string[]
  ) => {
    onFiltersChange({ ...filters, [field]: value });
  };

  // 数値範囲の変更
  const handleRangeChange = (
    field: 'messageCount' | 'responseTime',
    values: number[]
  ) => {
    onFiltersChange({
      ...filters,
      [field]: { min: values[0], max: values[1] },
    });
  };

  // フィルターリセット
  const handleClearFilters = () => {
    onFiltersChange({
      searchTerm: '',
      dateRange: { start: null, end: null },
      status: [],
      priority: [],
      tags: [],
      assignedTo: [],
      messageCount: { min: 0, max: 100 },
      responseTime: { min: 0, max: 24 },
      quickFilters: [],
    });
  };

  // アコーディオン展開切り替え
  const toggleAccordion = (panel: string) => {
    setExpandedAccordions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(panel)) {
        newSet.delete(panel);
      } else {
        newSet.add(panel);
      }
      return newSet;
    });
  };

  // アクティブフィルター数を計算
  const activeFilterCount = 
    filters.status.length + 
    filters.priority.length + 
    filters.tags.length + 
    filters.assignedTo.length + 
    filters.quickFilters.length +
    (filters.dateRange.start || filters.dateRange.end ? 1 : 0);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Box sx={{ p: compactMode ? 1 : 2 }}>
        {/* 検索バー */}
        <Accordion 
          expanded={expandedAccordions.has('search')}
          onChange={() => toggleAccordion('search')}
          elevation={0}
          sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
        >
          <AccordionSummary sx={{ px: 0, minHeight: 48 }}>
            <TextField
              fullWidth
              placeholder="メッセージ、顧客名、件名で検索..."
              value={filters.searchTerm}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                      color={activeFilterCount > 0 ? 'primary' : 'default'}
                    >
                      <FilterIcon />
                      {activeFilterCount > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            minWidth: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: 'error.main',
                            color: 'white',
                            fontSize: '0.6rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {activeFilterCount}
                        </Box>
                      )}
                    </IconButton>
                    {filters.searchTerm && (
                      <IconButton
                        size="small"
                        onClick={() => onFiltersChange({ ...filters, searchTerm: '' })}
                      >
                        <ClearIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </AccordionSummary>
        </Accordion>

        {/* クイックフィルター */}
        <Accordion 
          expanded={expandedAccordions.has('quick')}
          onChange={() => toggleAccordion('quick')}
          elevation={0}
          sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 0 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              クイックフィルター
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
              {QUICK_FILTERS.map((filter) => (
                <Chip
                  key={filter.id}
                  label={`${filter.icon} ${filter.label}`}
                  variant={filters.quickFilters.includes(filter.id) ? 'filled' : 'outlined'}
                  color={filters.quickFilters.includes(filter.id) ? 'primary' : 'default'}
                  size="small"
                  onClick={() => toggleQuickFilter(filter.id)}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* 高度なフィルターメニュー */}
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={() => setFilterMenuAnchor(null)}
          PaperProps={{
            sx: { width: 320, maxHeight: 400, overflow: 'auto' },
          }}
        >
          {/* 日付範囲 */}
          <MenuItem disableRipple>
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                📅 日付範囲
              </Typography>
              <Stack spacing={1}>
                <DateTimePicker
                  label="開始日時"
                  value={filters.dateRange.start}
                  onChange={(date) => handleDateRangeChange('start', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <DateTimePicker
                  label="終了日時"
                  value={filters.dateRange.end}
                  onChange={(date) => handleDateRangeChange('end', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Stack>
            </Box>
          </MenuItem>

          <Divider />

          {/* ステータス */}
          <MenuItem disableRipple>
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                📊 ステータス
              </Typography>
              <FormGroup>
                {STATUS_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={filters.status.includes(option.value)}
                        onChange={(e) => {
                          const newStatus = e.target.checked
                            ? [...filters.status, option.value]
                            : filters.status.filter(s => s !== option.value);
                          handleMultiSelectChange('status', newStatus);
                        }}
                        size="small"
                      />
                    }
                    label={option.label}
                  />
                ))}
              </FormGroup>
            </Box>
          </MenuItem>

          <Divider />

          {/* 優先度 */}
          <MenuItem disableRipple>
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                🔥 優先度
              </Typography>
              <FormGroup>
                {PRIORITY_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={filters.priority.includes(option.value)}
                        onChange={(e) => {
                          const newPriority = e.target.checked
                            ? [...filters.priority, option.value]
                            : filters.priority.filter(p => p !== option.value);
                          handleMultiSelectChange('priority', newPriority);
                        }}
                        size="small"
                      />
                    }
                    label={option.label}
                  />
                ))}
              </FormGroup>
            </Box>
          </MenuItem>

          <Divider />

          {/* メッセージ数範囲 */}
          <MenuItem disableRipple>
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                💬 メッセージ数: {filters.messageCount.min} - {filters.messageCount.max}
              </Typography>
              <Slider
                value={[filters.messageCount.min, filters.messageCount.max]}
                onChange={(_, values) => handleRangeChange('messageCount', values as number[])}
                min={0}
                max={100}
                step={5}
                marks={[
                  { value: 0, label: '0' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100+' },
                ]}
                valueLabelDisplay="auto"
                size="small"
              />
            </Box>
          </MenuItem>

          <Divider />

          {/* フィルターリセット */}
          <MenuItem>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              startIcon={<ClearIcon />}
              onClick={() => {
                handleClearFilters();
                setFilterMenuAnchor(null);
              }}
            >
              フィルターをクリア
            </Button>
          </MenuItem>
        </Menu>

        {/* アクティブフィルター表示 */}
        {activeFilterCount > 0 && (
          <Box sx={{ mt: 1 }}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
              {filters.quickFilters.map((filterId) => {
                const filter = QUICK_FILTERS.find(f => f.id === filterId);
                return filter ? (
                  <Chip
                    key={filterId}
                    label={filter.label}
                    size="small"
                    onDelete={() => toggleQuickFilter(filterId)}
                    color="primary"
                    variant="filled"
                  />
                ) : null;
              })}
              {filters.status.map((status) => (
                <Chip
                  key={`status-${status}`}
                  label={`ステータス: ${STATUS_OPTIONS.find(s => s.value === status)?.label}`}
                  size="small"
                  onDelete={() => handleMultiSelectChange('status', filters.status.filter(s => s !== status))}
                  color="secondary"
                />
              ))}
              {filters.priority.map((priority) => (
                <Chip
                  key={`priority-${priority}`}
                  label={`優先度: ${PRIORITY_OPTIONS.find(p => p.value === priority)?.label}`}
                  size="small"
                  onDelete={() => handleMultiSelectChange('priority', filters.priority.filter(p => p !== priority))}
                  color="warning"
                />
              ))}
              {(filters.dateRange.start || filters.dateRange.end) && (
                <Chip
                  label="日付範囲"
                  size="small"
                  onDelete={() => handleDateRangeChange('start', null)}
                  color="info"
                />
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default SearchFilters;
